// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import message from 'antd/lib/message';
import Modal from 'antd/lib/modal';
import notification from 'antd/lib/notification';
import Result from 'antd/lib/result';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import copy from 'copy-to-clipboard';
import { CheckOutlined, MinusOutlined, MoreOutlined } from '@ant-design/icons';

import { getCore, Reference, Task } from 'cvat-core-wrapper';
import { CombinedState } from 'reducers';
import { deleteTaskAsync } from 'actions/tasks-actions';
import { getInferenceStatusAsync } from 'actions/models-actions';
import ActionsMenuContainer from 'containers/actions-menu/actions-menu';
import ModelRunnerModal from 'components/model-runner-modal/model-runner-dialog';
import MoveTaskModal from 'components/move-task-modal/move-task-modal';
import { openAddImagesModal } from 'components/annotation-page/workspace/add-images-modal';
import LibraryLine from 'components/annotation-page/workspace/steps/library-line';
import { fetchSteps, StepsState, updateTaskStepSettings } from 'components/annotation-page/workspace/steps/steps-api';
import { fetchScene, Scene } from 'components/annotation-page/workspace/scene/scene-api';
import { folderTable } from 'components/annotation-page/workspace/scene/scene-model';
import { fetchProjectFolder, ProjectFolder } from 'components/project-page/project-page';
import ReferenceRow from 'components/project-page/reference-row';
import { taskPath } from 'components/project-page/task-row';
import { DetailPageSkeleton, RowSkeletons, StepsSkeleton } from 'components/common/skeletons';
import { t } from 'utils/i18n';
import AddPhotosModal from './add-photos-modal';
import BugTrackerEditor from './bug-tracker-editor';
import FrameTiles from './frame-tiles';
import LinkReferenceModal from './link-reference-modal';
import SceneSection from './scene-section';
import {
    createdLabel, plural, stepNote, stepsLabel, stepsSummary, waitingLine,
} from './task-model';
import UserSelector from './user-selector';

const core = getCore();

function TaskPageComponent(): JSX.Element {
    const id = +useParams<{ id: string }>().id;
    const history = useHistory();
    const dispatch = useDispatch();
    const deletes = useSelector((state: CombinedState) => state.tasks.activities.deletes);
    const [task, setTask] = useState<Task | null>(null);
    const [loading, setLoading] = useState(true);
    const [steps, setSteps] = useState<StepsState | null>(null);
    const [scene, setScene] = useState<Scene | null>(null);
    // null until the server answered: the empty state must not flash meanwhile
    const [references, setReferences] = useState<Reference[] | null>(null);
    const [folder, setFolder] = useState<ProjectFolder | null>(null);
    const [projectName, setProjectName] = useState<string | null>(null);
    // a failed request ends its skeleton with a message instead of leaving it forever
    const [failed, setFailed] = useState<{ steps?: boolean; references?: boolean; scene?: boolean }>({});
    // the projects list may already know the project's name: the breadcrumb does not wait for it
    const listedName = useSelector((state: CombinedState) => (
        state.projects.current.find((item: { id: number }) => item.id === task?.projectId)?.name
    ));
    const [saving, setSaving] = useState(false);
    const [adding, setAdding] = useState(false);
    const [linking, setLinking] = useState(false);

    const loadReferences = useCallback((): void => {
        core.references.get({ taskId: id })
            .then((list: Reference[]) => setReferences([...list]))
            .catch(() => setFailed((prev) => ({ ...prev, references: true })));
    }, [id]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        core.tasks.get({ id })
            .then(([loaded]: Task[]) => {
                if (cancelled || !loaded) return;
                setTask(loaded);
                if (loaded.projectId) {
                    core.projects.get({ id: loaded.projectId })
                        .then(([project]: { name: string }[]) => {
                            if (!cancelled && project) setProjectName(project.name);
                        })
                        .catch(() => undefined);
                    fetchProjectFolder(loaded.projectId)
                        .then((value) => { if (!cancelled) setFolder(value); })
                        .catch(() => undefined);
                }
            })
            .catch(() => undefined)
            .finally(() => { if (!cancelled) setLoading(false); });
        fetchSteps(id)
            .then((state) => { if (!cancelled) setSteps(state); })
            .catch(() => { if (!cancelled) setFailed((prev) => ({ ...prev, steps: true })); });
        fetchScene(id)
            .then((value) => { if (!cancelled) setScene(value); })
            .catch(() => { if (!cancelled) setFailed((prev) => ({ ...prev, scene: true })); });
        loadReferences();
        dispatch(getInferenceStatusAsync());
        return () => { cancelled = true; };
    }, [id]);

    useEffect(() => {
        if (task && deletes[id]) history.push(task.projectId ? `/projects/${task.projectId}` : '/projects');
    }, [deletes]);

    if (loading) {
        return (
            <DetailPageSkeleton
                primary={t('Open workspace')}
                strip={{ title: t('Drawings'), action: t('Add drawings') }}
                middle={{ title: t('Steps'), body: <StepsSkeleton rows={4} /> }}
                last={t('References')}
            />
        );
    }
    if (!task) {
        return <Result className='cvat-not-found' status='404' title='The task was not found' />;
    }

    const save = async (apply: (target: Task) => void): Promise<void> => {
        setSaving(true);
        try {
            apply(task);
            setTask(await task.save());
        } catch (error: unknown) {
            notification.error({ message: t('Could not update the task'), description: String(error) });
        } finally {
            setSaving(false);
        }
    };
    const setLibraries = async (libraries: string[]): Promise<void> => {
        setSaving(true);
        try {
            setSteps(await updateTaskStepSettings(id, { libraries }));
        } catch (error: unknown) {
            notification.error({ message: t('Could not change the libraries'), description: String(error) });
        } finally {
            setSaving(false);
        }
    };
    const confirmDelete = (): void => {
        Modal.confirm({
            title: t('Delete the task “{name}”?', { name: task.name }),
            content: t('The drawings and their annotations are removed. Linked references stay.'),
            okText: t('Delete'),
            okButtonProps: { danger: true },
            onOk: () => dispatch(deleteTaskAsync(task)),
        });
    };

    const workspace = taskPath(task);
    const job = task.jobs.find((item) => !item.type || item.type === 'annotation') ?? task.jobs[0];
    const summary = stepsSummary(steps);
    const waiting = waitingLine(summary?.waiting ?? []);
    const shownPath = folder ? folder.windows_path ?? folder.path : null;
    const folders = scene ? folderTable(scene) : null;
    const copyPath = (): void => {
        if (shownPath && copy(shownPath)) message.success(t('Path copied'));
        else message.error(t('Could not copy the path'));
    };
    const referencesLabel = (list: Reference[] | null): string => {
        if (list === null) return '';
        return list.length ? plural(list.length, 'reference') : t('no photos yet');
    };
    const meta = [
        plural(task.size, 'drawing'),
        referencesLabel(references),
        stepsLabel(summary),
    ].filter(Boolean).join(' · ');

    return (
        <div className='cvat-task-page'>
            <div className='cvat-task-page-content'>
                <nav aria-label={t('Breadcrumb')} className='cvat-task-page-crumbs'>
                    <Link to='/projects'>{t('Projects')}</Link>
                    <span>/</span>
                    {task.projectId ? (
                        <Link to={`/projects/${task.projectId}`}>{listedName ?? projectName ?? '…'}</Link>
                    ) : <span>{t('No project')}</span>}
                    <span>/</span>
                    <span className='cvat-task-page-crumb-current'>{task.name}</span>
                </nav>
                <div className='cvat-task-page-heading'>
                    <div className='cvat-task-page-heading-text'>
                        <Title
                            level={1}
                            editable={{
                                onChange: (value: string): void => {
                                    const name = value.trim();
                                    if (name && name !== task.name) save((target) => { target.name = name; });
                                },
                            }}
                        >
                            {task.name}
                        </Title>
                        <Text type='secondary'>{meta}</Text>
                    </div>
                    <Button type='primary' onClick={() => history.push(workspace)}>{t('Open workspace')}</Button>
                    <Dropdown
                        destroyPopupOnHide
                        trigger={['click']}
                        overlay={(
                            <ActionsMenuContainer
                                taskInstance={task}
                                onViewAnalytics={() => history.push(`/tasks/${id}/analytics`)}
                            />
                        )}
                    >
                        <Button aria-label={t('More actions')} icon={<MoreOutlined />} />
                    </Dropdown>
                </div>

                <div className='cvat-task-page-columns'>
                    <div className='cvat-task-page-main'>
                        <section aria-label={t('Drawings')} className='cvat-task-page-section'>
                            <div className='cvat-task-page-section-head'>
                                <h2>{t('Drawings')}</h2>
                                <Text type='secondary'>
                                    {task.size > 6 ?
                                        t('{count} · the first few; all of them are in the workspace', { count: task.size }) :
                                        String(task.size)}
                                </Text>
                                <span className='cvat-task-page-spacer' />
                                <Button onClick={() => openAddImagesModal(`/tasks/${id}/data`, 'drawings')}>{t('Add drawings')}</Button>
                            </div>
                            {job && task.size > 0 ? (
                                <FrameTiles
                                    session='jobs'
                                    jobId={job.id}
                                    count={task.size}
                                    status={steps?.frame_status}
                                    path={workspace}
                                />
                            ) : (
                                <Text type='secondary'>{t('No drawings yet.')}</Text>
                            )}
                        </section>

                        <section aria-label={t('Steps')} className='cvat-task-page-section'>
                            <div className='cvat-task-page-section-head'>
                                <h2>{t('Steps')}</h2>
                                {summary && (
                                    <Text type='secondary'>
                                        {t('{done} of {total} done', { done: summary.done, total: summary.total })}
                                    </Text>
                                )}
                                <span className='cvat-task-page-spacer' />
                                <Link to={workspace}>{t('Run in the workspace →')}</Link>
                            </div>
                            {summary && (
                                <div className='cvat-task-page-segments' aria-hidden>
                                    {summary.shown.map((step) => (
                                        <span
                                            key={step.id}
                                            className={`cvat-task-page-segment cvat-task-page-segment-${step.status}`}
                                        />
                                    ))}
                                </div>
                            )}
                            {!summary && !failed.steps && <StepsSkeleton rows={4} />}
                            {failed.steps && <Text type='secondary'>{t('Could not load the steps.')}</Text>}
                            <ul className='cvat-task-page-steps'>
                                {(summary?.shown ?? []).map((step) => (
                                    <li key={step.id} className='cvat-task-page-step' data-step={step.id}>
                                        <span
                                            className={
                                                `cvat-task-page-step-icon cvat-task-page-step-icon-${step.status}`
                                            }
                                            aria-label={step.status}
                                            role='img'
                                        />
                                        <span className='cvat-task-page-step-title'>{t(step.title)}</span>
                                        <Text type='secondary'>{stepNote(step, 'drawing')}</Text>
                                    </li>
                                ))}
                            </ul>
                            {waiting && <Text type='secondary' className='cvat-task-page-waiting'>{waiting}</Text>}
                        </section>

                        <SceneSection
                            taskId={task.id}
                            workspace={workspace}
                            scene={scene}
                            failed={!!failed.scene}
                            backendAPI={core.config.backendAPI}
                        />

                        <section aria-label={t('References')} className='cvat-task-page-section'>
                            <div className='cvat-task-page-section-head'>
                                <h2>{t('References')}</h2>
                                <Text type='secondary'>{t('photos of the rooms')}</Text>
                                {references && references.length > 0 && (
                                    <>
                                        <span className='cvat-task-page-spacer' />
                                        <Button onClick={() => setAdding(true)}>{t('Add photos')}</Button>
                                        <Button onClick={() => setLinking(true)}>{t('Link existing')}</Button>
                                    </>
                                )}
                            </div>
                            {references === null && !failed.references && (
                                <RowSkeletons count={Math.max(1, (task.references ?? []).length)} />
                            )}
                            {failed.references && <Text type='secondary'>{t('Could not load the references.')}</Text>}
                            {references && references.length > 0 && (
                                <div className='cvat-project-task-list'>
                                    {references.map((reference) => (
                                        <ReferenceRow
                                            key={reference.id}
                                            reference={reference}
                                            taskNames={{ [task.id]: task.name }}
                                        />
                                    ))}
                                </div>
                            )}
                            {references && references.length === 0 && (
                                <div className='cvat-task-page-empty'>
                                    <Text type='secondary'>
                                        {t('No photos yet. Furniture from the photos is matched to the library and placed on the drawings.')}
                                    </Text>
                                    <div className='cvat-task-page-empty-actions'>
                                        <Button type='primary' onClick={() => setAdding(true)}>{t('Add photos')}</Button>
                                        <Button onClick={() => setLinking(true)}>{t('Link an existing reference')}</Button>
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>

                    <aside aria-label={t('About this task')} className='cvat-task-page-aside'>
                        <div className='cvat-task-page-fact'>
                            <Text type='secondary'>{t('Assignee')}</Text>
                            <UserSelector
                                value={task.assignee}
                                ariaLabel={t('Assignee')}
                                onSelect={(user) => save((target) => { target.assignee = user; })}
                            />
                        </div>
                        <div className='cvat-task-page-fact'>
                            <Text type='secondary'>{t('Created')}</Text>
                            <span>{createdLabel(task.createdDate, task.owner?.username)}</span>
                        </div>
                        {task.projectId && (
                            <div className='cvat-task-page-fact'>
                                <Text type='secondary'>{t('Project folder')}</Text>
                                {shownPath ? (
                                    <>
                                        <span className='cvat-task-page-path' title={shownPath}>{shownPath}</span>
                                        <Button type='link' size='small' onClick={copyPath}>{t('Copy path')}</Button>
                                    </>
                                ) : (
                                    <Link to={`/projects/${task.projectId}/settings`}>{t('Choose a folder')}</Link>
                                )}
                                {folders && (
                                    <table className='cvat-task-scene-folders'>
                                        <thead>
                                            <tr>
                                                <th aria-label={t('Folder')} />
                                                <th>FP</th>
                                                <th>VR</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {folders.map((row) => (
                                                <tr key={row.name}>
                                                    <td>{row.name}</td>
                                                    {[row.FP, row.VR].map((present, index) => (
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        <td key={index}>
                                                            {present ?
                                                                <CheckOutlined aria-label={t('present')} /> :
                                                                <MinusOutlined aria-label={t('missing')} />}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                        {steps && (
                            <div className='cvat-task-page-fact'>
                                <Text type='secondary'>{t('Library for matching')}</Text>
                                <LibraryLine
                                    libraries={steps.libraries ?? []}
                                    fallback={t('the references only')}
                                    busy={saving}
                                    taskId={task.id}
                                    onChange={setLibraries}
                                />
                            </div>
                        )}
                        <div className='cvat-task-page-fact'>
                            {/* the editor carries its own label */}
                            <BugTrackerEditor
                                instance={task}
                                onChange={(value) => save((target) => { target.bugTracker = value; })}
                            />
                        </div>
                        <div className='cvat-task-page-danger'>
                            <Button type='link' danger onClick={confirmDelete}>{t('Delete task…')}</Button>
                        </div>
                    </aside>
                </div>
            </div>
            <ModelRunnerModal />
            <MoveTaskModal onUpdateTask={(updated: Task) => updated.save().then(setTask)} />
            {adding && task.projectId && (
                <AddPhotosModal
                    projectId={task.projectId}
                    taskId={id}
                    taskName={task.name}
                    onClose={() => setAdding(false)}
                    onCreated={() => { setAdding(false); loadReferences(); }}
                />
            )}
            {linking && task.projectId && (
                <LinkReferenceModal
                    projectId={task.projectId}
                    taskId={id}
                    linked={(references ?? []).map((reference) => reference.id)}
                    onClose={() => setLinking(false)}
                    onLinked={() => { setLinking(false); loadReferences(); }}
                />
            )}
        </div>
    );
}

export default React.memo(TaskPageComponent);
